import { useState } from 'react';
import { toast } from 'react-toastify';
import { FiAlertTriangle, FiCheckCircle, FiSearch } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import { personApi } from '../../../../services/personApi';
import {
    maskDocument,
    unmaskDocument,
} from '../../credit-analysis/utils/documentUtils';
import { maskPhone } from '../../../../utils/phoneUtils';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { Beneficiary, SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import { canSaveBeneficiary, notFoundMessage, personLinkMessage } from './beneficiaryPersonLink';
import { prefillContacts } from './beneficiaryContacts';

interface BeneficiaryFormModalProps {
    // Presente = edição; ausente = criação.
    beneficiary?: Beneficiary | null;
    typeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (data: {
        name: string; beneficiaryType: string; document?: string; personId?: string;
        email?: string; phone?: string; mobile?: string; description?: string;
    }) => Promise<boolean>;
    onCancel: () => void;
}

const INLINE_FIELDS = new Set([
    'name', 'beneficiaryType', 'document', 'email', 'phone', 'mobile', 'description',
]);

const isCompleteDocument = (doc: string): boolean => {
    const len = unmaskDocument(doc).length;
    return len === 11 || len === 14;
};

// Modal de cadastro/edição de beneficiário (registro global). Nome obrigatório; tipo,
// documento e observações completam o cadastro. Darkmode e ConfirmDialog seguem o padrão do
// módulo.
const BeneficiaryFormModal = ({
    beneficiary,
    typeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: BeneficiaryFormModalProps) => {
    const [name, setName] = useState(beneficiary?.name || '');
    const [beneficiaryType, setBeneficiaryType] = useState(
        beneficiary?.beneficiaryType || typeOptions[0]?.value || 'OTHER',
    );
    const [document, setDocument] = useState(
        beneficiary?.document ? maskDocument(beneficiary.document) : '',
    );
    const [email, setEmail] = useState(beneficiary?.email || '');
    const [phone, setPhone] = useState(beneficiary?.phone ? maskPhone(beneficiary.phone) : '');
    const [mobile, setMobile] = useState(beneficiary?.mobile ? maskPhone(beneficiary.mobile) : '');
    const [description, setDescription] = useState(beneficiary?.description || '');
    // Vínculo ao cadastro person: preenchido pela busca por documento. Quando presente, nome e
    // documento vêm do person e ficam travados (o registro passa a espelhar o cadastro central).
    const [personId, setPersonId] = useState<string | undefined>(beneficiary?.personId || undefined);
    const [searching, setSearching] = useState(false);

    const linkedToPerson = !!personId;
    const canSave = canSaveBeneficiary(beneficiaryType, personId);

    const changeDocument = (raw: string) => {
        setDocument(maskDocument(raw));
    };

    const searchPerson = async () => {
        if (!isCompleteDocument(document)) return;
        setSearching(true);
        try {
            const person = await personApi.findPersonByDocument(document);
            if (!person) {
                toast.info(notFoundMessage(beneficiaryType));
                return;
            }
            // Achou: espelha o cadastro central. Nome e documento passam a vir do person
            // (travados); o vínculo é guardado. Os contatos do cadastro preenchem só os campos
            // vazios e continuam editáveis.
            setPersonId(person.uuid);
            setName(person.name || name);
            if (person.document) setDocument(maskDocument(person.document));
            const contacts = prefillContacts({ email, phone, mobile }, person.contacts);
            setEmail(contacts.email);
            setPhone(contacts.phone);
            setMobile(contacts.mobile);
            toast.success('Cadastro encontrado e vinculado.');
        } catch {
            toast.error('Falha ao consultar Pessoas. Tente novamente.');
        } finally {
            setSearching(false);
        }
    };

    const submit = async () => {
        await onSubmit({
            name,
            beneficiaryType,
            document: unmaskDocument(document) || undefined,
            personId,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
            mobile: mobile.trim() || undefined,
            description: description.trim() || undefined,
        });
    };

    const residual = errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onCancel} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-4 space-y-3">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {beneficiary ? 'Editar beneficiário' : 'Novo beneficiário'}
                    </h3>

                    {/* Documento + busca no cadastro person: se o CPF/CNPJ já existe, vincula e
                        reaproveita nome/documento (travados). Se não, segue o cadastro básico. */}
                    <div className="space-y-1">
                        <div className="flex items-end gap-2">
                            <div className="flex-1">
                                <TextEdit
                                    label="Documento (CNPJ/CPF, opcional)"
                                    value={document}
                                    onChange={(e) => changeDocument(e.target.value)}
                                    disabled={linkedToPerson}
                                    errorMessage={getErrorMessage(errorFields, 'document')}
                                />
                            </div>
                            {!linkedToPerson && (
                                <button
                                    type="button"
                                    onClick={searchPerson}
                                    disabled={searching || !isCompleteDocument(document)}
                                    title="Buscar no cadastro de pessoas"
                                    className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
                                >
                                    <FiSearch size={14} /> {searching ? 'Buscando...' : 'Buscar'}
                                </button>
                            )}
                        </div>
                        {linkedToPerson && (
                            <div className="flex items-center justify-between text-xs text-green-700 dark:text-green-300">
                                <span className="flex items-center gap-1">
                                    <FiCheckCircle size={12} /> Vinculado ao cadastro de pessoas
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setPersonId(undefined)}
                                    className="text-gray-500 dark:text-gray-400 hover:underline"
                                >
                                    Desvincular
                                </button>
                            </div>
                        )}
                    </div>

                    {linkedToPerson ? (
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Nome (cadastro em Pessoas)</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white break-words">{name}</p>
                        </div>
                    ) : (
                        <TextEdit
                            label="Nome"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            errorMessage={getErrorMessage(errorFields, 'name')}
                        />
                    )}
                    <SelectTextField
                        label="Tipo"
                        items={typeOptions}
                        value={beneficiaryType}
                        onChange={(e) => setBeneficiaryType(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'beneficiaryType')}
                    />
                    <TextEdit
                        label="E-mail (opcional)"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'email')}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <TextEdit
                            label="Telefone (opcional)"
                            value={phone}
                            onChange={(e) => setPhone(maskPhone(e.target.value))}
                            errorMessage={getErrorMessage(errorFields, 'phone')}
                        />
                        <TextEdit
                            label="Celular (opcional)"
                            value={mobile}
                            onChange={(e) => setMobile(maskPhone(e.target.value))}
                            errorMessage={getErrorMessage(errorFields, 'mobile')}
                        />
                    </div>
                    <TextEdit
                        label="Observações (opcional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'description')}
                    />

                    {!canSave && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                            <FiAlertTriangle size={14} className="mt-0.5 shrink-0" />
                            <span>{personLinkMessage(beneficiaryType)}</span>
                        </div>
                    )}

                    {residual.map((e) => (
                        <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                            <FiAlertTriangle size={14} />
                            <span>{e.message}</span>
                        </div>
                    ))}

                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={isSaving || !canSave}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                        >
                            {isSaving ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BeneficiaryFormModal;
